import { useEffect, useRef, useState } from 'react';
import { Plus, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { accountService } from '@/services';
import type { AccountResponseDto } from '@/types';
import type { SelectedAccountRef } from '../system-admin-ui-config';

type InputMode = 'search' | 'id';

interface AddAccountControlProps {
  selectedIds: string[];
  onAdd: (account: SelectedAccountRef) => void;
  atCapacity?: boolean;
}

// Search + by-ID add control. Stays open across adds so the list can be built in one pass.
export function AddAccountControl({ selectedIds, onAdd, atCapacity }: AddAccountControlProps) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<InputMode>('search');
  const [accountIdInput, setAccountIdInput] = useState('');
  const [search, setSearch] = useState('');
  const [accounts, setAccounts] = useState<AccountResponseDto[]>([]);
  const [loading, setLoading] = useState(false);
  const selected = new Set(selectedIds);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const reqIdRef = useRef(0);

  // Debounced server-side search (only on the search tab). `loading` flips on the
  // keystroke; a request id drops responses that resolve out of order.
  useEffect(() => {
    if (!open || mode !== 'search') return;
    const reqId = ++reqIdRef.current;
    setLoading(true);
    const t = setTimeout(() => {
      accountService
        .getAllAccounts({ page: 1, limit: 100, search: search.trim() || undefined })
        .then((data) => reqId === reqIdRef.current && setAccounts(data.accounts))
        .catch(() => reqId === reqIdRef.current && setAccounts([]))
        .finally(() => reqId === reqIdRef.current && setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [search, open, mode]);

  // Land focus on the search box (not the tab) so typing + arrow/enter work immediately.
  useEffect(() => {
    if (open && mode === 'search') {
      const t = setTimeout(() => searchInputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open, mode]);

  const handleIdSubmit = () => {
    const trimmed = accountIdInput.trim();
    if (!trimmed || atCapacity || selected.has(trimmed)) return;
    // Resolve a name for the label; add regardless.
    accountService
      .getAllAccounts({ page: 1, limit: 100, search: trimmed })
      .then((data) => onAdd({ id: trimmed, name: data.accounts.find((a) => a.id === trimmed)?.name }))
      .catch(() => onAdd({ id: trimmed }));
    setAccountIdInput('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant='outline' className='gap-2 rounded-xl'>
          <Plus className='h-4 w-4' />
          Add account
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[300px] p-0' align='end' onOpenAutoFocus={(e) => e.preventDefault()}>
        <div className='p-2 border-b'>
          <Tabs value={mode} onValueChange={(v) => setMode(v as InputMode)}>
            <TabsList className='w-full'>
              <TabsTrigger value='search' className='flex-1 text-xs'>Search</TabsTrigger>
              <TabsTrigger value='id' className='flex-1 text-xs'>Account ID</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {atCapacity && (
          <p className='px-3 py-2 text-[11px] text-amber-700 bg-amber-50'>
            Up to 10 accounts for now — remove one to add another.
          </p>
        )}

        {mode === 'id' ? (
          <div className='p-2 flex gap-2'>
            <Input
              placeholder='Enter account ID'
              value={accountIdInput}
              onChange={(e) => setAccountIdInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleIdSubmit();
              }}
              className='h-8 text-sm'
              disabled={atCapacity}
              autoFocus
            />
            <Button size='sm' className='h-8 shrink-0' onClick={handleIdSubmit} disabled={atCapacity}>
              Add
            </Button>
          </div>
        ) : (
          <Command shouldFilter={false}>
            <CommandInput
              ref={searchInputRef}
              placeholder='Search accounts...'
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {loading ? (
                <div className='flex items-center justify-center gap-2 py-6 text-xs text-muted-foreground'>
                  <Loader2 className='h-4 w-4 animate-spin' />
                  Searching…
                </div>
              ) : accounts.length === 0 ? (
                <div className='py-6 text-center text-xs text-muted-foreground'>No accounts found.</div>
              ) : (
                <CommandGroup>
                  {accounts.map((account) => {
                    const already = selected.has(account.id);
                    return (
                      <CommandItem
                        key={account.id}
                        value={`${account.name} ${account.id}`}
                        disabled={already || atCapacity}
                        onSelect={() => onAdd({ id: account.id, name: account.name })}
                      >
                        <div className='flex flex-1 flex-col'>
                          <span className='text-sm'>{account.name}</span>
                          <span className='text-xs text-muted-foreground'>
                            ID {account.id}
                            {account.sub_domain ? ` · ${account.sub_domain}` : ''}
                          </span>
                        </div>
                        {already && <Check className='ml-2 h-4 w-4 text-green-500' />}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
}
